import { Helmet } from "react-helmet-async";
import React, { useState, useEffect } from "react";
import TranslationDisplay from "../TranslationDisplay/TranslationDisplay";

import "./Demos.css";
import "../../styles/colors.css";
import { motion } from "framer-motion";

const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

const Demos = ({ demosConfigPath }) => {
  const [demosData, setDemosData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!demosConfigPath) {
      setError("No demosConfigPath provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(demosConfigPath)
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(
              `Fetch failed with status ${response.status}: ${text.slice(
                0,
                200
              )}...`
            );
          });
        }
        return response.json();
      })
      .then((data) => {
        setDemosData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching demos config:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [demosConfigPath]);

  // Helper to format language list
  const formatLanguages = (from, translatedArr) => {
    if (!Array.isArray(translatedArr) || translatedArr.length === 0) return "";
    const langs = translatedArr.map((t) => t.language).filter(Boolean);
    if (langs.length === 1) {
      return `From ${from} to ${langs[0]}`;
    }
    if (langs.length === 2) {
      return `From ${from} to ${langs[0]} and ${langs[1]}`;
    }
    // 3 or more
    return `From ${from} to ${langs.slice(0, -1).join(", ")}, and ${
      langs[langs.length - 1]
    }`;
  };

  if (error) {
    return (
      <div className="demos-container">
        <div className="max-w-7xl mx-auto px-4">
          <header className="demos-header">
            <h1 className="demos-title">Error Loading Demos</h1>
            <p className="demos-subtitle">{error}</p>
          </header>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="demos-container">
        <div className="max-w-7xl mx-auto px-4">
          <header className="demos-header">
            <h1 className="demos-title">Loading Demos...</h1>
          </header>
        </div>
      </div>
    );
  }

  if (!demosData.length) {
    return (
      <div className="demos-container">
        <div className="max-w-7xl mx-auto px-4">
          <header className="demos-header">
            <h1 className="demos-title">No Demos Available</h1>
          </header>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Video Translation Demos – LingoLab</title>
        <meta
          name="description"
          content="See real examples of LingoLab's AI-powered video translation and localization. Watch demo videos translated into multiple languages with native voice and lip sync."
        />
        <meta
          name="keywords"
          content="AI video translation demos, multilingual video samples, video localization, LingoLab demos"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/demos" />
        <meta
          property="og:title"
          content="LingoLab | Video Translation Demos"
        />
        <meta
          property="og:description"
          content="Watch LingoLab's AI video translation demos and discover seamless multilingual video localization."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/demos" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta
          name="twitter:title"
          content="LingoLab | Video Translation Demos"
        />
        <meta
          name="twitter:description"
          content="Watch LingoLab's AI video translation demos and discover seamless multilingual video localization."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>

      <div className="demos-container">
        <div className="max-w-7xl mx-auto px-4">
          <motion.header
            className="demos-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h1 className="demos-title">Explore Our Demos</h1>
            <p className="demos-subtitle">
              Discover the power of seamless video translations
            </p>
            <div className="header-divider"></div>
          </motion.header>
          <div className="demos-grid">
            {demosData.map((demo, index) => (
              <div key={index} className="demo-cell">
                <TranslationDisplay configPath={demo.configPath} />
                <div className="translation-info">
                  {/* Load config and show language info */}
                  <DemoTranslationInfo configPath={demo.configPath} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// Helper component to fetch config and display language info
const DemoTranslationInfo = ({ configPath }) => {
  const [info, setInfo] = useState("");

  useEffect(() => {
    let isMounted = true;
    fetch(configPath)
      .then((res) => res.json())
      .then((config) => {
        // Support both {videos: [...]} and direct video object
        const video =
          Array.isArray(config.videos) && config.videos.length
            ? config.videos[0]
            : config;
        if (!video) return;
        const from = video.translatedFrom || "Unknown";
        const translatedArr = video.translated || [];
        const langs = translatedArr.map((t) => t.language).filter(Boolean);
        let text = "";
        if (langs.length === 1) {
          text = `From ${from} to ${langs[0]}`;
        } else if (langs.length === 2) {
          text = `From ${from} to ${langs[0]} and ${langs[1]}`;
        } else if (langs.length > 2) {
          text = `From ${from} to ${langs.slice(0, -1).join(", ")}, and ${
            langs[langs.length - 1]
          }`;
        }
        if (isMounted) setInfo(text);
      });
    return () => {
      isMounted = false;
    };
  }, [configPath]);

  return <>{info}</>;
};

export default Demos;
